import { ColorCircle } from '../WidgetHeaderChannelPicker/ColorCircle';
import { WidgetHeaderElementLabel } from '../WidgetHeaderElementLabel';
import { WidgetHeaderIconButton } from '../WidgetHeaderIconButton';
import { NewWidgetHeaderItem, WidgetHeaderOverflowSubMenuTrigger } from '../WidgetHeaderItem';
import { faCircleHalfStroke } from '@fortawesome/pro-light-svg-icons/faCircleHalfStroke';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSignals } from '@preact/signals-react/runtime';
import type { MenubarProps } from '@radix-ui/react-menubar';
import { cn, cva } from '@thinkalpha/ui-core/utils';
import type { VariantProps } from '@thinkalpha/ui-core/utils';
import React, { useCallback } from 'react';
import {
    DropdownMenuItem,
    DropdownMenuSub,
    DropdownMenuSubContent,
    MenubarContent,
    MenubarItem,
    MenubarMenu,
    MenubarTrigger,
} from 'src/components/ui';
import { MenuSub, MenuSubTrigger, MenuSubContent, MenuItemIcon, MenuItem } from 'src/components/ui/Menu';
import { container } from 'src/StaticContainer';
import type { ChannelChangeEvent, ChannelSignal, ResetChannelDataEvent } from 'src/types/EventBus';
import type { EventBus } from 'src/types/EventBus/EventBus';
import { eventBusSupports } from 'src/types/EventBus/EventBus';

export const channelVariants = cva('rounded-none', {
    variants: {
        bg: {
            transparent: 'bg-secondary-foreground focus:bg-secondary/80 dark:bg-secondary dark:focus:bg-secondary/80',
            blue: 'bg-sky-500 focus:bg-blue-600 dark:focus:bg-blue-400',
            green: 'bg-emerald-500 focus:bg-emerald-600 dark:focus:bg-emerald-400',
            red: 'bg-rose-500 focus:bg-rose-600 dark:focus:bg-rose-400',
            purple: 'bg-violet-500 focus:bg-violet-600 dark:focus:bg-violet-400',
            orange: 'bg-orange-500 focus:bg-orange-600 dark:focus:bg-orange-400',
            // this obviously isn't brown, but I think it'd require a migration if anybody was actually using any brown channels
            brown: 'bg-fuchsia-500 focus:bg-fuchsia-600 dark:focus:bg-fuchsia-400',
            yellow: 'bg-yellow-500 focus:bg-yellow-600 dark:focus:bg-yellow-400',
        },
        fill: {
            transparent: 'text-transparent',
            blue: 'fill-sky-500 text-sky-500',
            green: 'fill-emerald-500 text-emerald-500',
            red: 'fill-rose-500 text-rose-500',
            purple: 'fill-violet-500 text-violet-500',
            orange: 'fill-orange-500 text-orange-500',
            brown: 'fill-fuchsia-500 text-fuchsia-500',
            yellow: 'fill-yellow-500 text-yellow-500',
        },
        // Mirrors the focus: colors from bg, but for data-highlighted attribute (set by menu reducer)
        highlighted: {
            transparent: 'data-highlighted:bg-secondary/80 dark:data-highlighted:bg-secondary/80',
            blue: 'data-highlighted:bg-blue-600 dark:data-highlighted:bg-blue-400',
            green: 'data-highlighted:bg-emerald-600 dark:data-highlighted:bg-emerald-400',
            red: 'data-highlighted:bg-rose-600 dark:data-highlighted:bg-rose-400',
            purple: 'data-highlighted:bg-violet-600 dark:data-highlighted:bg-violet-400',
            orange: 'data-highlighted:bg-orange-600 dark:data-highlighted:bg-orange-400',
            brown: 'data-highlighted:bg-fuchsia-600 dark:data-highlighted:bg-fuchsia-400',
            yellow: 'data-highlighted:bg-yellow-600 dark:data-highlighted:bg-yellow-400',
        },
    },
});

export const channelColors: VariantProps<typeof channelVariants>['bg'][] = [
    'orange',
    'yellow',
    'green',
    'blue',
    'purple',
    'brown',
    'red',
];

export const externalAppLinkingChannelId = 'red';
export const externalAppLinkingLabel = 'External';

export const isChannelColor = (color: string | null | undefined): color is VariantProps<typeof channelVariants>['bg'] =>
    color ? channelColors.some((channel) => channel === color) : false;

interface ChannelPickerProps {
    onChannelChange: (channelId: string | null, e: React.MouseEvent) => void;
    onResetChannelData: () => void;
    activeChannel: string | null;
    variant?: 'overflow' | 'standard';
}

export const ChannelPickerMenu = ({
    onChannelChange,
    activeChannel,
    onResetChannelData,
    variant = 'standard',
}: ChannelPickerProps) => {
    const onSelectColor = useCallback(
        (channelId: string | null | undefined, e: React.MouseEvent) => {
            onChannelChange?.(channelId ?? null, e);
        },
        [onChannelChange],
    );
    const Comp = variant == 'overflow' ? DropdownMenuItem : MenubarItem;
    return (
        <>
            <Comp
                key={'null'}
                className={cn(channelVariants({ bg: 'transparent' }), 'place-content-center')}
                onClick={(e) => onSelectColor(null, e)}
            >
                Unlinked
            </Comp>
            {activeChannel && (
                <Comp
                    key={'reset'}
                    className={cn(channelVariants({ bg: 'transparent' }), 'place-content-center')}
                    onClick={() => onResetChannelData?.()}
                >
                    Clear Channel Data
                </Comp>
            )}
            {channelColors.map((color) => (
                <Comp
                    key={color}
                    className={cn(
                        channelVariants({ bg: color }),
                        (!APP_ELECTRON || externalAppLinkingChannelId !== color) &&
                            'text-transparent focus:text-transparent',
                    )}
                    onClick={(e) => onSelectColor(color, e)}
                >
                    {APP_ELECTRON && externalAppLinkingChannelId === color ? externalAppLinkingLabel : color}
                </Comp>
            ))}
        </>
    );
};

interface WidgetHeaderChannelPickerProps extends MenubarProps {
    eventBus: EventBus<ChannelSignal, ChannelChangeEvent & ResetChannelDataEvent>;
}
export const WidgetHeaderChannelPicker = ({
    eventBus,
    children,
    className,
    ...props
}: WidgetHeaderChannelPickerProps) => {
    useSignals();
    const channelId = eventBus.getSignal('widget::channel').value;

    return (
        <MenubarMenu {...props}>
            <WidgetHeaderIconButton asChild>
                <MenubarTrigger className={className}>
                    <ColorCircle className={isChannelColor(channelId) ? channelVariants({ fill: channelId }) : null} />
                    {children}
                </MenubarTrigger>
            </WidgetHeaderIconButton>
            <MenubarContent className="min-w-[6rem]">
                <WidgetHeaderChannelPickerContent eventBus={eventBus} />
            </MenubarContent>
        </MenubarMenu>
    );
};

const trackChannelLinked = (eventBus: EventBus<any, any>, channel: string | null) => {
    if (eventBusSupports(eventBus, ['widget::type'] as const, [] as const)) {
        container.getOptional('AnalyticsService')?.track({
            type: 'channel_linked',
            widgetType: eventBus.getSignal('widget::type').value,
            channel,
        });
    }
};

const WidgetHeaderChannelPickerContent = ({
    eventBus,
    variant = 'standard',
    onChannelSelect,
}: WidgetHeaderChannelPickerProps & {
    variant?: 'overflow' | 'standard';
    onChannelSelect?: (e: React.MouseEvent) => void;
}) => {
    const channelId = eventBus.getSignal('widget::channel').value;

    return (
        <ChannelPickerMenu
            onChannelChange={(channelId: string | null, e: React.MouseEvent) => {
                eventBus.emit('widget::channelChange', { channel: channelId });
                trackChannelLinked(eventBus, channelId);
                onChannelSelect?.(e);
            }}
            activeChannel={channelId}
            onResetChannelData={() => eventBus.emit('widget::resetChannelData', undefined)}
            variant={variant}
        />
    );
};

type WidgetHeaderChannelPickerOverflowProps = {
    eventBus: EventBus<ChannelSignal, ChannelChangeEvent & ResetChannelDataEvent>;
};
export const WidgetHeaderChannelPickerOverflow = ({ eventBus }: WidgetHeaderChannelPickerOverflowProps) => {
    useSignals();
    const channelId = eventBus.getSignal('widget::channel').value;

    return (
        <DropdownMenuSub>
            <NewWidgetHeaderItem asChild>
                <WidgetHeaderOverflowSubMenuTrigger>
                    <div className="aspect-square h-4">
                        <ColorCircle
                            className={isChannelColor(channelId) ? channelVariants({ fill: channelId }) : null}
                        />
                    </div>
                    <WidgetHeaderElementLabel>Select Channel</WidgetHeaderElementLabel>
                </WidgetHeaderOverflowSubMenuTrigger>
            </NewWidgetHeaderItem>
            <DropdownMenuSubContent className="min-w-[6rem]">
                <WidgetHeaderChannelPickerContent
                    onChannelSelect={(e) => e.preventDefault()}
                    eventBus={eventBus}
                    variant="overflow"
                    className="text-base"
                />
            </DropdownMenuSubContent>
        </DropdownMenuSub>
    );
};

/**
 * Menu overflow variant - renders as a floating-ui MenuSub inside a Menu context.
 */
export const WidgetHeaderChannelPickerMenuOverflow = ({ eventBus }: WidgetHeaderChannelPickerOverflowProps) => {
    useSignals();
    const channelId = eventBus.getSignal('widget::channel').value;

    const emitChannelChange = useCallback(
        (channel: string | null) => {
            eventBus.emit('widget::channelChange', { channel });
            trackChannelLinked(eventBus, channel);
        },
        [eventBus],
    );

    return (
        <MenuSub>
            <MenuSubTrigger>
                <MenuItemIcon>
                    <ColorCircle className={isChannelColor(channelId) ? channelVariants({ fill: channelId }) : null} />
                </MenuItemIcon>
                Select Channel
            </MenuSubTrigger>
            <MenuSubContent className="min-w-24">
                <MenuItem
                    onClick={() => emitChannelChange(null)}
                    className={cn(
                        channelVariants({ bg: 'transparent', highlighted: 'transparent' }),
                        'place-content-center',
                    )}
                >
                    Unlinked
                </MenuItem>
                {channelId && (
                    <MenuItem
                        onClick={() => eventBus.emit('widget::resetChannelData', undefined)}
                        className={cn(
                            channelVariants({ bg: 'transparent', highlighted: 'transparent' }),
                            'place-content-center',
                        )}
                    >
                        Clear Channel Data
                    </MenuItem>
                )}
                {channelColors.map((color) => (
                    <MenuItem
                        key={color}
                        onClick={() => emitChannelChange(color!)}
                        className={cn(
                            channelVariants({ bg: color, highlighted: color }),
                            (!APP_ELECTRON || externalAppLinkingChannelId !== color) &&
                                'text-transparent focus:text-transparent',
                        )}
                    >
                        {APP_ELECTRON && externalAppLinkingChannelId === color ? externalAppLinkingLabel : color}
                    </MenuItem>
                ))}
            </MenuSubContent>
        </MenuSub>
    );
};

export const WidgetHeaderChannelPickerMeasure = () => {
    return (
        <MenubarMenu>
            <WidgetHeaderIconButton asChild>
                <MenubarTrigger>
                    <FontAwesomeIcon icon={faCircleHalfStroke} className={cn('h-3 w-3')} fixedWidth />
                </MenubarTrigger>
            </WidgetHeaderIconButton>
        </MenubarMenu>
    );
};
